import { useMemo, useState } from 'react';
import { nettoyerHtml } from './EditeurRiche';
import { urlExempleModele } from '../services/modeleContratService';
import PanneauApercu from './PanneauApercu';
import '../assets/styles/SelecteurModele.css';
import { IconeOeil } from '../components/IconesDocument';

/**
 * Choix d'un modèle de contrat, façon explorateur de fichiers : une
 * recherche par nom en haut, les modèles en cartes, et pour chacun un
 * aperçu de son contenu avant de s'engager.
 *
 * Le composant ne fait que choisir : c'est le parent qui applique.
 */
function SelecteurModeleContrat({ modeles, valeur, onChoisir, onAppliquer, disabled = false }) {
  const [recherche, setRecherche] = useState('');
  const [apercu, setApercu] = useState(null);
  // Le PDF d'exemple du modèle, quand il en a un (Paramétrage > Modèles).
  const [apercuPdf, setApercuPdf] = useState(null);

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return modeles;
    return modeles.filter((m) =>
      [m.libelle, m.sousTitre, m.referenceLegale].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [modeles, recherche]);

  const choisi = modeles.find((m) => String(m.id) === String(valeur)) || null;

  return (
    <div className="selmod">
      <div className="selmod-barre">
        <input
          type="search"
          className="selmod-recherche"
          placeholder="Rechercher un modèle par nom…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          disabled={disabled}
        />
        <button
          type="button"
          className="devis-btn devis-btn-sm devis-btn-primary"
          onClick={onAppliquer}
          disabled={disabled || !choisi}
          title={choisi ? `Appliquer « ${choisi.libelle} » à ce contrat` : 'Choisissez un modèle'}
        >
          {choisi ? 'Appliquer ce modèle' : 'Appliquer'}
        </button>
      </div>

      {visibles.length === 0 && (
        <div className="selmod-vide">
          {modeles.length ? 'Aucun modèle ne correspond.' : 'Aucun modèle de contrat : créez-en dans Paramétrage.'}
        </div>
      )}

      <div className="selmod-grille">
        {visibles.map((m) => {
          const actif = choisi && choisi.id === m.id;
          return (
            <div
              key={m.id}
              className={`selmod-carte${actif ? ' selmod-carte-active' : ''}`}
              onClick={() => !disabled && onChoisir(String(m.id))}
              onDoubleClick={() => !disabled && setApercu(m)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!disabled) onChoisir(String(m.id));
                }
              }}
              aria-pressed={Boolean(actif)}
            >
              <div className="selmod-icone" aria-hidden="true">
                <svg width="34" height="40" viewBox="0 0 34 40" fill="none">
                  <path d="M4 2h18l8 8v28H4z" fill="#fff" stroke="#2f2560" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M22 2v8h8" fill="#e9eff5" stroke="#2f2560" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M9 18h16M9 23h16M9 28h10" stroke="#7050cf" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <div className="selmod-nom" title={m.libelle}>
                {m.libelle}
              </div>
              <div className="selmod-sous">{m.sousTitre || m.referenceLegale || ' '}</div>
              <div className="selmod-meta">
                {m.articles.length} article{m.articles.length > 1 ? 's' : ''}
              </div>
              <div className="selmod-boutons">
                <button
                  type="button"
                  className="selmod-apercu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setApercu(m);
                  }}
                  disabled={disabled}
                >
                  Aperçu
                </button>
                {m.exemplePdf && (
                  <button
                    type="button"
                    className="selmod-apercu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setApercuPdf(m);
                    }}
                    disabled={disabled}
                    title={m.exemplePdfNom || "Voir le PDF d'exemple"}
                  >
                    <IconeOeil />
                    PDF
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {apercuPdf && (
        <PanneauApercu
          titre={apercuPdf.libelle}
          sousTitre={apercuPdf.exemplePdfNom || "PDF d'exemple"}
          url={urlExempleModele(apercuPdf.id, { apercu: true })}
          onClose={() => setApercuPdf(null)}
        />
      )}

      {apercu && (
        <div className="param-modal-overlay" onClick={() => setApercu(null)}>
          <div className="param-modal selmod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">{apercu.libelle}</h2>
              <button type="button" className="param-modal-close" onClick={() => setApercu(null)}>
                ×
              </button>
            </div>
            <div className="param-modal-body selmod-lecture">
              <div className="selmod-lecture-titre">{apercu.titreDoc}</div>
              {apercu.sousTitre && <div className="selmod-lecture-sous">{apercu.sousTitre}</div>}
              {apercu.referenceLegale && (
                <div className="selmod-lecture-ref">({apercu.referenceLegale})</div>
              )}
              {apercu.preambule && (
                // Contenu écrit par un utilisateur du back-office, nettoyé par
                // la même liste blanche que le serveur.
                <div
                  className="selmod-lecture-corps"
                  dangerouslySetInnerHTML={{ __html: nettoyerHtml(apercu.preambule) }}
                />
              )}
              {apercu.articles.map((a, i) => (
                <div className="selmod-lecture-article" key={i}>
                  <h3>
                    {a.numero ? `${a.numero}. ` : ''}
                    {a.titre}
                  </h3>
                  <div
                    className="selmod-lecture-corps"
                    dangerouslySetInnerHTML={{ __html: nettoyerHtml(a.corps) }}
                  />
                </div>
              ))}
              <p className="selmod-lecture-note">
                Les balises <code>{'{{…}}'}</code> sont remplacées par les données du contrat au
                rendu.
              </p>
            </div>
            <div className="param-form-actions selmod-modal-actions">
              <button type="button" className="param-btn" onClick={() => setApercu(null)}>
                Fermer
              </button>
              <button
                type="button"
                className="param-btn param-btn-primary"
                onClick={() => {
                  onChoisir(String(apercu.id));
                  setApercu(null);
                }}
                disabled={disabled}
              >
                Choisir ce modèle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SelecteurModeleContrat;
